<template>
  <div class="country-select-container" v-click-outside="closeDropdown">
    <div class="select-trigger" @click="toggleDropdown">
      <span v-if="selectedCount === 0" class="placeholder">
        {{ placeholder }}
      </span>
      <span v-else class="selected-text">
        {{ selectedText }}
      </span>
      <i class="fa fa-caret-down trigger-arrow"></i>
    </div>

    <div v-if="isOpen" class="dropdown-menu-custom">
      <div class="search-container">
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search countries..."
          class="search-input"
          @click.stop
        />
      </div>

      <div class="options-list">
        <div
          v-for="country in filteredOptions"
          :key="country.record_number"
          class="option-group"
        >
          <div class="option-item" @click.stop="toggleCountry(country)">
            <div class="option-left">
              <i
                v-if="country.states && country.states.length"
                class="fa"
                style="margin-right: 2px"
                :class="
                  isExpanded(country) ? 'fa-chevron-down' : 'fa-chevron-right'
                "
                @click.stop="toggleExpand(country)"
              ></i>
              <span class="country-name">{{ country.country_name }}</span>
              <span
                v-if="country.states && country.states.length"
                class="state-count ml-2"
              >
                ({{ country.states.length }})
              </span>
            </div>
            <div class="option-right">
              <input
                type="checkbox"
                :checked="isCountrySelected(country)"
                @click.stop
                @change="toggleCountry(country, $event.target.checked)"
              />
            </div>
          </div>

          <div v-if="isExpanded(country) && country.states" class="states-list">
            <div
              v-for="state in country.states"
              :key="state.record_number"
              class="state-item"
              @click.stop="toggleState(country, state)"
            >
              <div class="option-left">
                <i class="fa fa-map-marker state-icon mr-2"></i>
                <span class="state-name">
                  {{ state.state_name || state.name }}
                </span>
              </div>
              <div class="option-right">
                <input
                  type="checkbox"
                  :checked="isStateSelected(state)"
                  @click.stop
                  @change="toggleState(country, state, $event.target.checked)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="dropdown-footer">
        <button class="reset-all-btn" @click.stop="resetAll">
          <i class="fa fa-refresh"></i> Reset All
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CountrySelect",
  props: {
    options: {
      type: Array,
      default: () => []
    },
    value: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: "Select country"
    }
  },
  data() {
    return {
      isOpen: false,
      searchQuery: "",
      expandedCountries: []
    };
  },
  computed: {
    selectedCount() {
      const uniqueCountries = new Set();
      this.value.forEach(item => {
        if (item.isState) {
          uniqueCountries.add(item.parent_country);
        } else {
          uniqueCountries.add(item.country_name);
        }
      });
      return uniqueCountries.size;
    },
    selectedText() {
      if (this.value.length === 0) return this.placeholder;

      const uniqueCountries = new Set();
      this.value.forEach(item => {
        if (item.isState) {
          uniqueCountries.add(item.parent_country);
        } else {
          uniqueCountries.add(item.country_name);
        }
      });

      if (uniqueCountries.size > 1) {
        return `${uniqueCountries.size} countries selected`;
      }

      // Only one country involved
      if (this.value.length === 1) {
        const item = this.value[0];
        return item.state_name || item.name || item.country_name;
      }

      // Multiple items but all from the same country
      return Array.from(uniqueCountries)[0];
    },
    filteredOptions() {
      if (!this.searchQuery) return this.options;
      const query = this.searchQuery.toLowerCase();
      return this.options.filter(country => {
        const countryMatch = country.country_name.toLowerCase().includes(query);
        const stateMatch =
          country.states &&
          country.states.some(state =>
            (state.state_name || state.name || "").toLowerCase().includes(query)
          );
        return countryMatch || stateMatch;
      });
    }
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.$emit("open");
      } else {
        this.$emit("close");
      }
    },
    closeDropdown() {
      if (this.isOpen) {
        this.isOpen = false;
        this.$emit("close");
      }
    },
    isExpanded(country) {
      return this.expandedCountries.includes(country.record_number);
    },
    toggleExpand(country) {
      const index = this.expandedCountries.indexOf(country.record_number);
      if (index > -1) {
        this.expandedCountries.splice(index, 1);
      } else {
        this.expandedCountries.push(country.record_number);
      }
    },
    isCountrySelected(country) {
      const explicitlySelected = this.value.some(
        item => item.record_number == country.record_number && !item.isState
      );
      if (explicitlySelected) return true;

      if (country.states && country.states.length > 0) {
        return country.states.every(s =>
          this.value.some(
            item => item.record_number == s.record_number && item.isState
          )
        );
      }
      return false;
    },
    isStateSelected(state) {
      return this.value.some(
        item => item.record_number === state.record_number && item.isState
      );
    },
    toggleCountry(country, forceState = null) {
      let newValue = [...this.value];
      const isSelected =
        forceState !== null ? !forceState : this.isCountrySelected(country);
      const isAllCountries =
        country.record_number === "" || country.country_name === "All countries";

      if (isSelected) {
        // Deselecting country and all its states
        newValue = newValue.filter(
          item =>
            !(item.record_number === country.record_number && !item.isState) &&
            !(item.isState && item.parent_country === country.country_name)
        );
      } else {
        // Selecting country
        if (isAllCountries) {
          newValue = [{ ...country, isState: false }];
        } else {
          // Remove "All countries" if it exists
          newValue = newValue.filter(
            item =>
              item.country_name !== "All countries" &&
              item.record_number !== ""
          );

          // Add country if not already there
          if (!this.isCountrySelected(country)) {
            newValue.push({ ...country, isState: false });
          }

          // Add all its states
          if (country.states && country.states.length) {
            country.states.forEach(state => {
              const alreadySelected = newValue.some(
                item =>
                  item.record_number === state.record_number && item.isState
              );
              if (!alreadySelected) {
                newValue.push({
                  ...state,
                  isState: true,
                  parent_country: country.country_name
                });
              }
            });
          }
        }
      }
      this.$emit("input", newValue);
    },
    toggleState(country, state, forceState = null) {
      let newValue = [...this.value];
      const isSelected =
        forceState !== null ? !forceState : this.isStateSelected(state);

      if (isSelected) {
        // Deselecting state
        newValue = newValue.filter(
          item => !(item.record_number === state.record_number && item.isState)
        );
        // Also deselect the parent country if it was selected
        newValue = newValue.filter(
          item =>
            !(item.record_number === country.record_number && !item.isState)
        );
      } else {
        // Selecting state
        // Remove "All countries" if it exists
        newValue = newValue.filter(
          item =>
            item.country_name !== "All countries" &&
            item.record_number !== ""
        );

        newValue.push({
          ...state,
          isState: true,
          parent_country: country.country_name
        });

        // Check if all states of this country are now selected
        const allStatesSelected = country.states.every(s =>
          newValue.some(
            item => item.record_number === s.record_number && item.isState
          )
        );
        if (allStatesSelected) {
          const countrySelected = newValue.some(
            item =>
              item.record_number === country.record_number && !item.isState
          );
          if (!countrySelected) {
            newValue.push({ ...country, isState: false });
          }
        }
      }
      this.$emit("input", newValue);
    },
    resetAll() {
      this.$emit("input", []);
      this.searchQuery = "";
    }
  }
};
</script>

<style scoped>
.country-select-container {
  position: relative;
  width: 100%;
  font-family: "Poppins", sans-serif;
}

.select-trigger {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;  
  background-color: #fff;
  cursor: pointer;
  min-height: 40px;
}

.placeholder {
  color: #c0c4cc;
  font-size: 14px;
}

.selected-text {
  color: #606266;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.trigger-arrow {
  color: #c0c4cc;
  font-size: 12px;
}

.dropdown-menu-custom {
  position: absolute;
  top: 100%;
  left: -13px;
  z-index: 1000;
  width: 100%;
  min-width: 300px;
  margin-top: 5px;
  background-color: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  max-height: 400px;
  display: flex;
  flex-direction: column;
}

.search-container {
  padding: 10px;
  border-bottom: 1px solid #f2f6fc;
}

.search-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 14px;
  outline: none;
}

.search-input:focus {
  border-color: #409eff;
}

.options-list {
  flex: 1;
  overflow-y: auto;
  padding: 5px 0;
  max-height: 250px;
}

.option-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 15px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.option-item:hover {
  background-color: #f5f7fa;
}

.option-left {
  display: flex;
  align-items: center;
  /* gap: 8px; */
}

.fa-chevron-right,
.fa-chevron-down {
  font-size: 10px;
  color: #909399;
  width: 12px;
}

.country-name {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
}

.state-count {
  font-size: 12px;
  color: #909399;
}

.states-list {
  background-color: #fafafa;
}

.state-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 15px 8px 35px;
  cursor: pointer;
}

.state-item:hover {
  background-color: #f0f2f5;
}

.state-icon {
  font-size: 12px;
  color: #909399;
}

.state-name {
  font-size: 13px;
  color: #606266;
}

.dropdown-footer {
  padding: 10px;
  border-top: 1px solid #f2f6fc;
  display: flex;
  justify-content: center;
}

.reset-all-btn {
  width: 100%;
  padding: 8px;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  color: #606266;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.reset-all-btn:hover {
  color: #409eff;
  border-color: #c6e2ff;
  background-color: #ecf5ff;
}

.reset-all-btn i {
  font-size: 12px;
}

input[type="checkbox"] {
  cursor: pointer;
}
</style>
